import { ChevronDown } from 'lucide-react'
import { useState } from 'react'
import { clsx } from 'clsx'
import { CATEGORY_GROUPS, getCategoriesByGroup } from '@/utils/categories'
import { NZ_REGIONS } from '@/utils/constants'
import { PRICE_PRESETS, type ListingFilters } from '@/types/filters'

interface FilterSidebarProps {
  filters: ListingFilters
  onFilterChange: (key: keyof ListingFilters, value: string) => void
  onClearFilters: () => void
  showVehicleFilters?: boolean
}

interface FilterSectionProps {
  title: string
  children: React.ReactNode
  defaultOpen?: boolean
}

function FilterSection({ title, children, defaultOpen = true }: FilterSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className="border-b border-slate-200 py-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full text-left"
        aria-expanded={isOpen}
      >
        <span className="font-medium text-slate-900">{title}</span>
        <ChevronDown
          className={clsx(
            'w-4 h-4 text-slate-400 transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>
      {isOpen && <div className="mt-3">{children}</div>}
    </div>
  )
}

export function FilterSidebar({
  filters,
  onFilterChange,
  onClearFilters,
  showVehicleFilters = false,
}: FilterSidebarProps) {
  return (
    <aside className="w-64 flex-shrink-0">
      <div className="sticky top-20 bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-lg">Filters</h2>
          <button
            onClick={onClearFilters}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            Clear all
          </button>
        </div>

        {/* Category */}
        <FilterSection title="Category">
          <select
            value={filters.group}
            onChange={(e) => {
              onFilterChange('group', e.target.value)
              onFilterChange('category', '')
            }}
            className="input mb-2"
            aria-label="Category group"
          >
            <option value="">All Categories</option>
            {CATEGORY_GROUPS.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>

          {filters.group && (
            <select
              value={filters.category}
              onChange={(e) => onFilterChange('category', e.target.value)}
              className="input"
              aria-label="Category"
            >
              <option value="">All in {CATEGORY_GROUPS.find(g => g.id === filters.group)?.name}</option>
              {getCategoriesByGroup(filters.group).map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          )}
        </FilterSection>

        {/* Location */}
        <FilterSection title="Location">
          <select
            value={filters.location}
            onChange={(e) => onFilterChange('location', e.target.value)}
            className="input"
            aria-label="Location"
          >
            <option value="">All New Zealand</option>
            {NZ_REGIONS.map((region) => (
              <option key={region} value={region}>
                {region}
              </option>
            ))}
          </select>
        </FilterSection>

        {/* Price Range */}
        <FilterSection title="Price">
          <div className="flex flex-wrap gap-2 mb-3">
            {PRICE_PRESETS.map((preset) => (
              <button
                key={preset.label}
                onClick={() => {
                  onFilterChange('priceMin', preset.min.toString())
                  onFilterChange('priceMax', preset.max?.toString() || '')
                }}
                className={clsx(
                  'text-xs px-2 py-1 rounded-full border transition-colors',
                  filters.priceMin === preset.min.toString()
                    ? 'bg-primary-500 text-white border-primary-500'
                    : 'border-slate-300 hover:border-primary-500'
                )}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="number"
              placeholder="Min"
              value={filters.priceMin}
              onChange={(e) => onFilterChange('priceMin', e.target.value)}
              className="input"
              aria-label="Minimum price"
            />
            <input
              type="number"
              placeholder="Max"
              value={filters.priceMax}
              onChange={(e) => onFilterChange('priceMax', e.target.value)}
              className="input"
              aria-label="Maximum price"
            />
          </div>
        </FilterSection>

        {/* Condition */}
        <FilterSection title="Condition" defaultOpen={false}>
          <div className="space-y-2">
            {['new', 'excellent', 'good', 'fair', 'used'].map((condition) => (
              <label key={condition} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="condition"
                  value={condition}
                  checked={filters.condition === condition}
                  onChange={(e) => onFilterChange('condition', e.target.value)}
                  className="text-primary-500 focus:ring-primary-500"
                />
                <span className="text-sm capitalize">{condition}</span>
              </label>
            ))}
            <button
              onClick={() => onFilterChange('condition', '')}
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              Clear
            </button>
          </div>
        </FilterSection>

        {/* Sale Type */}
        <FilterSection title="Sale Type" defaultOpen={false}>
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="saleType"
                value="buynow"
                checked={filters.saleType === 'buynow'}
                onChange={(e) => onFilterChange('saleType', e.target.value)}
                className="text-primary-500 focus:ring-primary-500"
              />
              <span className="text-sm">Buy Now</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="saleType"
                value="auction"
                checked={filters.saleType === 'auction'}
                onChange={(e) => onFilterChange('saleType', e.target.value)}
                className="text-primary-500 focus:ring-primary-500"
              />
              <span className="text-sm">Auction</span>
            </label>
            <button
              onClick={() => onFilterChange('saleType', '')}
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              Clear
            </button>
          </div>
        </FilterSection>

        {/* Vehicle-specific filters */}
        {showVehicleFilters && (
          <>
            <FilterSection title="Make & Model" defaultOpen={false}>
              <input
                type="text"
                placeholder="Make (e.g., Toyota)"
                value={filters.make}
                onChange={(e) => onFilterChange('make', e.target.value)}
                className="input mb-2"
                aria-label="Vehicle make"
              />
              <input
                type="text"
                placeholder="Model (e.g., Corolla)"
                value={filters.model}
                onChange={(e) => onFilterChange('model', e.target.value)}
                className="input"
                aria-label="Vehicle model"
              />
            </FilterSection>

            <FilterSection title="Year" defaultOpen={false}>
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="From"
                  value={filters.yearMin}
                  onChange={(e) => onFilterChange('yearMin', e.target.value)}
                  className="input"
                  aria-label="Minimum year"
                />
                <input
                  type="number"
                  placeholder="To"
                  value={filters.yearMax}
                  onChange={(e) => onFilterChange('yearMax', e.target.value)}
                  className="input"
                  aria-label="Maximum year"
                />
              </div>
            </FilterSection>

            <FilterSection title="Transmission" defaultOpen={false}>
              <select
                value={filters.transmission}
                onChange={(e) => onFilterChange('transmission', e.target.value)}
                className="input"
                aria-label="Transmission type"
              >
                <option value="">Any</option>
                <option value="Automatic">Automatic</option>
                <option value="Manual">Manual</option>
              </select>
            </FilterSection>
          </>
        )}

      </div>
    </aside>
  )
}
